import { useState, useRef, useCallback, useEffect } from "react";

import {
  RecaptchaVerifier,
  PhoneAuthProvider,
  updatePhoneNumber,
} from "firebase/auth";
import { useFirebase } from "firebase";
import {
  phoneUpdateCodeEnded,
  phoneUpdateSendFailureMessage,
  type SendTrigger,
} from "components/OtpVerification/ended-code";
import { getFriendlyErrorMessage } from "utils/errors";

/** How checking a code went: the number was updated, the code was wrong or
 * the check failed, or the provider ended the code, which only a new one
 * replaces. */
export type PhoneUpdateResult = "updated" | "failed" | "code_ended";

// This hook specifically handles updating the phone number for an *already authenticated* user.
// It uses PhoneAuthProvider.verifyPhoneNumber and updatePhoneNumber.
export function usePhoneUpdateVerification() {
  const [isVerifying, setIsVerifying] = useState(false); // General loading state for OTP sending/verification
  const [error, setError] = useState<string | null>(null);
  const [verificationId, setVerificationId] = useState<string | null>(null); // Stores the ID from verifyPhoneNumber
  const { auth } = useFirebase(); // Get auth instance
  const recaptchaVerifierRef = useRef<RecaptchaVerifier | null>(null);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Reusable reCAPTCHA cleanup logic (similar to useSMSVerification)
  const cleanupRecaptcha = useCallback(() => {
    try {
      if (recaptchaVerifierRef.current) {
        recaptchaVerifierRef.current.clear();
        recaptchaVerifierRef.current = null;
      }
      // Only remove container if it was potentially created by this hook instance (check needed?)
      // Or assume the container is persistent and managed elsewhere (e.g., in AccountPage)
      // For now, let's not remove it to avoid conflicts if useSMSVerification is also used.

      // Reset global state cautiously if grecaptcha exists and has reset method
      if (window.grecaptcha?.reset) {
        // Check for grecaptcha and reset method directly
        try {
          const hasRecaptchaClients =
            document.querySelector(".grecaptcha-badge") ||
            document.querySelector('iframe[src*="recaptcha"]');
          if (hasRecaptchaClients) {
            console.log("Attempting grecaptcha.reset() in update hook");
            window.grecaptcha.reset(); // Attempt reset if clients found
          }
        } catch (e) {
          console.warn("Error trying to check/reset grecaptcha:", e);
        }
      }
    } catch (err) {
      console.error("Error during reCAPTCHA cleanup:", err);
    }
  }, []);

  // Effect for component unmount cleanup
  useEffect(() => {
    return () => {
      cleanupRecaptcha();
    };
  }, [cleanupRecaptcha]);

  // Step 1: Send OTP using PhoneAuthProvider.verifyPhoneNumber
  const startVerification = async (
    newPhoneNumber: string,
    trigger: SendTrigger = "resend",
  ): Promise<boolean> => {
    setIsVerifying(true);
    setError(null);
    setVerificationId(null); // Reset previous verification attempt

    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError("No user is currently signed in. Cannot update phone number.");
      setIsVerifying(false);
      return false;
    }

    try {
      const formattedPhone = newPhoneNumber.startsWith("+")
        ? newPhoneNumber
        : `+1${newPhoneNumber.replace(/\D/g, "")}`;

      // Ensure reCAPTCHA is initialized (invisible)
      if (!recaptchaVerifierRef.current) {
        console.log("Initializing reCAPTCHA for phone update");
        // Ensure the container exists
        const container = document.getElementById("recaptcha-container");
        if (!container) {
          console.error(
            'reCAPTCHA container not found. Ensure <div id="recaptcha-container"> is rendered.',
          );
          setError("Could not initialize verification. Please refresh.");
          setIsVerifying(false);
          return false;
        }

        // Initialize ONLY if the ref is null
        try {
          recaptchaVerifierRef.current = new RecaptchaVerifier(
            auth,
            "recaptcha-container",
            {
              size: "invisible",
              "expired-callback": () => {
                setError("Verification timeout. Please request a new code.");
                setVerificationId(null); // Invalidate current attempt
                cleanupRecaptcha(); // Clean up the expired verifier
              },
              "error-callback": (error: Error) => {
                console.error("reCAPTCHA error:", error);
                setError("reCAPTCHA verification failed. Please try again.");
                cleanupRecaptcha();
              },
            },
          );
          await recaptchaVerifierRef.current.render();
        } catch (error) {
          console.error("Error initializing/rendering reCAPTCHA:", error);
          setError("Failed to initialize verification process.");
          cleanupRecaptcha();
          setIsVerifying(false);
          return false;
        }
      }

      // Use PhoneAuthProvider to send the code
      const phoneAuthProvider = new PhoneAuthProvider(auth);
      const newVerificationId = await phoneAuthProvider.verifyPhoneNumber(
        formattedPhone,
        recaptchaVerifierRef.current!, // Should be initialized above
      );

      setVerificationId(newVerificationId); // Store the ID needed for step 2
      setError(null); // Clear previous errors
      console.log(
        "OTP Sent for update flow, verificationId:",
        newVerificationId,
      );
      return true; // OTP sending initiated successfully
    } catch (err: any) {
      console.error("Error starting phone number update verification:", err);
      setError(phoneUpdateSendFailureMessage(err.code || err.message, trigger));
      cleanupRecaptcha(); // Clean up on error
      return false;
    } finally {
      setIsVerifying(false);
    }
  };

  // Step 2: Verify the OTP code and update the user's phone number
  const verifyCode = async (code: string): Promise<PhoneUpdateResult> => {
    if (!verificationId) {
      return "code_ended";
    }
    const currentUser = auth.currentUser;
    if (!currentUser) {
      setError("No user is currently signed in.");
      return "failed";
    }

    setIsVerifying(true);
    setError(null);

    try {
      // Create the credential using the verification ID and the code
      const credential = PhoneAuthProvider.credential(verificationId, code);

      // Update the current user's phone number
      await updatePhoneNumber(currentUser, credential);

      console.log("Phone number updated successfully in Firebase.");
      setVerificationId(null); // Clear verification ID after successful update
      // Optionally cleanup reCAPTCHA here or let unmount handle it
      // cleanupRecaptcha();
      return "updated";
    } catch (err: any) {
      console.error("Error verifying code or updating phone number:", err);
      if (phoneUpdateCodeEnded(err.code)) return "code_ended";
      // Handle specific errors like invalid code, quota exceeded, number already exists etc.
      setError(getFriendlyErrorMessage(err.code || err.message));
      // Do NOT clear verificationId here, allow user to retry code entry if it was just a typo
      return "failed";
    } finally {
      setIsVerifying(false);
    }
  };

  return {
    isVerifying,
    error,
    clearError,
    startVerification, // Function to send OTP
    verifyCode, // Function to verify OTP and update number
  };
}
